import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore } from '@/stores/authStore';
import { toTitleCase } from '@utils/textCase';
import type { DocumentTypeDto } from '@/types/documentType';
import styles from './DocumentTypeModals.module.scss';

// ============================================
// TYPES
// ============================================

interface ViewDocumentTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: DocumentTypeDto | null;
  onEdit: () => void;
  onDelete: () => void;
  /** Optional — when set + AUDIT_READ granted, footer shows an Audit trail button. */
  onAudit?: () => void;
}

// ============================================
// COMPONENT
// ============================================

function ViewDocumentTypeModal({
  isOpen,
  onClose,
  data,
  onEdit,
  onDelete,
  onAudit,
}: ViewDocumentTypeModalProps) {
  const { t } = useTranslation();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  if (!data) return null;

  const statusClass = data.status === 'ACTIVE' ? styles.statusBadgeActive : styles.statusBadgeInactive;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={data.name}
      size="md"
      footer={
        <div className={styles.viewFooter}>
          {hasPermission('DOCUMENT_TYPES_DELETE') && data.status === 'ACTIVE' && (
            <Button variant="danger" size="sm" onClick={onDelete}>
              {t('K_DEACTIVATE', 'Deactivate')}
            </Button>
          )}
          <div className={styles.viewFooterRight}>
            <Button variant="outline" size="sm" onClick={onClose}>
              {t('K_CLOSE', 'Close')}
            </Button>
            {onAudit && hasPermission('AUDIT_READ') && (
              <Button variant="outline" size="sm" onClick={onAudit}>
                {t('K_AUDIT_TITLE', 'Audit trail')}
              </Button>
            )}
            {hasPermission('DOCUMENT_TYPES_UPDATE') && data.status !== 'INACTIVE' && (
              <Button variant="filled" size="sm" onClick={onEdit}>
                {t('K_EDIT', 'Edit')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className={styles.fieldGrid}>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_NAME', 'Name')}</span>
          <span className={styles.fieldValue}>{data.name}</span>
        </div>
        <div className={styles.fieldItemFull}>
          <span className={styles.fieldLabel}>{t('K_LABEL_DESCRIPTION', 'Description')}</span>
          <span className={styles.fieldValue}>{data.description || '-'}</span>
        </div>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_STATUS', 'Status')}</span>
          <span className={`${styles.statusBadge} ${statusClass}`}>
            {toTitleCase(data.status)}
          </span>
        </div>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_CREATED', 'Created')}</span>
          <span className={styles.fieldValue}>
            {data.createdAt ? new Date(data.createdAt).toLocaleDateString() : '-'}
          </span>
        </div>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_UPDATED', 'Updated')}</span>
          <span className={styles.fieldValue}>
            {data.updatedAt ? new Date(data.updatedAt).toLocaleDateString() : '-'}
          </span>
        </div>
      </div>
    </Modal>
  );
}

export default ViewDocumentTypeModal;
